import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Container,
  Spinner,
} from "reactstrap";

import tableStyles from "../../../../assets/scss/common/table.module.scss";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import { GetUserData, perPageData, showToastMessage } from "common/service/CommonService";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import Reason from "common/utilities/Reason/Reason";
import DeleteModal from "Components/Common/DeleteModal";
import { DrpOption, User, FormValues, RoleDropdownRecord } from "../types/user.types";
import { queryClient } from "api/queryClient";
import { useUserService } from "../hooks/useUserQuery";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import AddUser from "../components/AddUser";
import UpdateRole from "../components/UpdateRole";
import ViewUser from "../components/ViewUser";
import CommonButton from "../../../../Components/Common/CommonButton";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "common/constants/PermissionConstants";

/* ========================================
   Main Component
   ======================================== */

const UserListSSO: React.FC = () => {
  document.title = "Accounts | ZENTIXS VISTA";

  // ========================================
  // State
  // ========================================

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [selectedRecord, setSelectedRecord] =
    useState<Partial<User> | null>(null);
  const [editRecord, setEditRecord] =
    useState<Partial<FormValues> | null>(null);
  const [updatedRecord, setUpdatedRecord] = useState<Partial<FormValues> | null>(null);
  const [isReasonModal, setIsReasonModal] = useState(false);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);
  const [selectUserId, setSelectUserId] = useState<undefined | number | null>(null);

  // Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  // ========================================
  // Toggles
  // ========================================

  const toggleAddModal = () => setIsAddModalOpen((p) => !p);
  const toggleRoleModal = () => setIsRoleModalOpen((p) => !p);
  const toggleViewModal = () => setIsViewModalOpen((p) => !p);
  const toggleDeleteModal = () => {
    setDeleteModal((p) => !p);
    setShowDeleteReason(false);
  };
  const toggleReasonModal = () => setIsReasonModal((p) => !p);
  const toggleDeleteReason = () => setShowDeleteReason((p) => !p);

  const openAddUserModal = () => {
    setEditRecord(null);
    toggleAddModal();
  };

  // ========================================
  // Query Services
  // ========================================

  const { SelectAllQuery, SelectQuery, UserDropdownQuery, RoleDropdownQuery, InsertMutation, UpdateMutation, DeleteMutation } =
    useUserService({
      id: GetUserData()?.userid,
      searchText: searchText,
      pageNumber: currentPageNumber,
      pageSize: perPageData,
      userid: selectUserId,
      enabledUserSelectAll: true,
      enabledUserDropdown: isAddModalOpen,
      enabledRoleDropdown: isAddModalOpen || isRoleModalOpen,
      enabledUserSelect: Boolean(selectUserId),
    });

  const { data: USERList } = UserDropdownQuery;
  const { data: ROLEList } = RoleDropdownQuery;

  // ========================================
  // Dropdown Options
  // ========================================

  const userDropdownOptions: DrpOption[] = useMemo(
    () =>
      (USERList?.data ?? []).map((r: any) => ({
        value: r.userid,
        label: r.username,
      })),
    [USERList],
  );

  const roleDropdownOptions: DrpOption[] = useMemo(
    () =>
      (ROLEList?.data ?? []).map((r: RoleDropdownRecord) => ({
        value: r.roleid,
        label: r.role ?? r.title ?? r.rolename ?? "",
      })),
    [ROLEList],
  );

  // ========================================
  // Table Data
  // ========================================

  const { data, isLoading } = SelectAllQuery;

  const { data: selectedUser } = SelectQuery;

  // ========================================
  // Search Handler
  // ========================================

  const onSearch = (text: string) => {
    setCurrentPageNumber(1);
    setSearchText(text);
  };

  // ========================================
  // Save Handler
  // ========================================

  const onSave = ({ userid, roleid }: FormValues) => {
    const payload = {
      userid: userid,
      roleid: roleid,
    };

    InsertMutation.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["user-selectall"],
        });
        queryClient.invalidateQueries({
          queryKey: ["user-dropdown-selectall"],
        });
        toggleAddModal();
      },
      onError:(error:any)=>{showToastMessage("error",error)}
    });
  };

  // ========================================
  // Edit Handler
  // ========================================

  const handleRoleUpdate = (row: Partial<User>) => {
    const user = data?.users.find((a: any) => a.userid === row.userid);

    setEditRecord({
      userid: user?.userid,
      username: user?.username,
      roleid: user?.roleid,
      userroleid: user?.userroleid,
    });
    toggleRoleModal();
  };

  // ========================================
  // Update with Reason Handler
  // ========================================

  const onUpdateClick = (data: FormValues) => {
    if (!data?.userid || data.roleid === null) return;
    setUpdatedRecord(data);
    setIsReasonModal(true);
  };

  const btnReasonClick = (reasonPayload: {
    id: number | null;
    reason: string;
  }) => {
    if (!updatedRecord) return;

    const payload = {
      userroleid: reasonPayload.id,
      userid: updatedRecord.userid,
      roleid: updatedRecord.roleid,
      updatereason: reasonPayload.reason,
    };

    UpdateMutation.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["user-selectall"],
        });
        toggleReasonModal();
        setEditRecord(null);
        setUpdatedRecord(null);
        toggleRoleModal();
      },
    });
  };

  // ========================================
  // Delete Handler
  // ========================================

  const handleDeleteClick = (item: Partial<User>) => {
    setSelectedRecord(item);
    toggleDeleteModal();
  };

  const confirmDelete = (reasonPayload: {
    id: number | null;
    reason: string;
  }) => {
    if (!selectedRecord) return;

    DeleteMutation.mutate(
      { id: reasonPayload.id, userid: selectedRecord.userid, updatereason: reasonPayload.reason },
      {
        onSuccess: (res: any) => {
          if (res?.data === -1 || res?.statuscode === -1) {
            showToastMessage("error", "User not found.");
            return;
          }
          const totalRecords = data?.users?.[0]?.totalcount || 0;
          const updatedTotalRecords = totalRecords - 1;
          const lastPage = Math.ceil(updatedTotalRecords / perPageData);
          const updatedPage =
            lastPage === 0 ? 1 : Math.min(currentPageNumber, lastPage);
          setCurrentPageNumber(updatedPage);
          queryClient.invalidateQueries({
            queryKey: ["user-selectall"],
          });
          queryClient.invalidateQueries({
            queryKey: ["user-dropdown-selectall"],
          });

          setSelectedRecord(null);
          toggleDeleteModal();
          toggleDeleteReason();
        }
      },
    );
  };

  // ========================================
  // Audit Handler
  // ========================================

  const onAuditClick = () => {
    const payload = {
      modulename: "User",
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "User_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  // ========================================
  // View Handler
  // ========================================
  const handleViewClick = (item: Partial<User>) => {
    setSelectUserId(item.userid);
    toggleViewModal();
  };

  // ========================================
  // UI Render
  // ========================================

  return (
    <div className="page-content">
      <Container fluid className="px-0">
        <Card className={tableStyles.card}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <CommonSearch onSearch={onSearch} searchText={searchText} />
              <div className="d-flex gap-2">
                <CommonButton
                  module={PERMISSION_MODULES.USER}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  outline
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  size="sm"
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1" />
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  module={PERMISSION_MODULES.USER}
                  action={PERMISSION_ACTIONS.ASSIGN_ROLE}
                  color="primary"
                  outline
                  onClick={openAddUserModal}
                  size="sm"
                >
                  <i className="ri-add-line me-1" /> Add
                </CommonButton>
              </div>
            </div>

            <CommonTableIndexWise
              moduleName={PERMISSION_MODULES.USER}
              columns={[
                { key: "username", label: "User Name" },
                { key: "userdisplayname", label: "Full Name" },
                { key: "empcode", label: "Employee Code" },
                { key: "branname", label: "Branch" },
                { key: "department", label: "Department" },
                { key: "designation", label: "Designation" },
                { key: "role", label: "Role" },
                {
                  key: "statustext",
                  label: "Status",
                  render: (val: any, row?: any) => {
                    const status = (val || row?.statustext || "").trim();
                    if (!status) return "-";
                    const lower = status.toLowerCase();
                    const color =
                      lower === "active"
                        ? "success"
                        : "danger";
                    return (
                      <Badge
                        color={color}
                        className="fw-medium px-2 py-1"
                      >
                        {status}
                      </Badge>
                    );
                  },
                },
              ]}
              data={data?.users || []}
              loading={isLoading}
              pageSize={perPageData}
              currentPage={currentPageNumber}
              onPageChange={setCurrentPageNumber}
              totalRecords={data?.users?.[0]?.totalcount || 0}
              actions={{
                onView: handleViewClick,
                onDelete: handleDeleteClick,
                onAssignRole: handleRoleUpdate,
              }}
              showStatus={false}
            />
          </CardBody>
        </Card>

        {/* Add Modal */}
        <AddUser
          isOpen={isAddModalOpen}
          toggle={toggleAddModal}
          existingData={editRecord || undefined}
          onSave={onSave}
          onCancel={toggleAddModal}
          roleDropdownData={roleDropdownOptions}
          userDropdownData={userDropdownOptions}
          loading={InsertMutation.isPending}
        />

        {/* Update Role Modal */}
        <UpdateRole
          isOpen={isRoleModalOpen}
          toggle={toggleRoleModal}
          roleDropdownData={roleDropdownOptions}
          editRecord={editRecord}
          onSave={onUpdateClick}
        />

        {/* View User Modal */}
        <ViewUser
          isOpen={isViewModalOpen}
          toggle={toggleViewModal}
          selectedUser={selectedUser}
          loading={SelectQuery.isLoading}
        />

        {/* Reason Modal */}
        {isReasonModal && (
          <Reason
            isOpen={isReasonModal}
            toggle={toggleReasonModal}
            action="Update"
            id={editRecord?.userroleid ?? null}
            click={btnReasonClick}
            isLoading={UpdateMutation.isPending}
          />
        )}

        {/* Delete Modal */}
        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            toggle={toggleDeleteModal}
            recordId={selectedRecord?.userroleid ?? 0}
            recordName={selectedRecord?.username}
            onDeleteClick={confirmDelete}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={toggleDeleteReason}
            isLoading={DeleteMutation.isPending}
          />
        )}
      </Container>

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </div>
  );
};

export default UserListSSO;
